import React, { FC, useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  FormHelperText,
  Paper,
} from '@material-ui/core';
import Icon from '@mdi/react';
import {
  mdiArchiveOutline,
  mdiFlaskOutline,
  mdiFormatListBulletedType,
  mdiKey,
  mdiRuler,
  mdiTableColumnWidth,
  mdiTableRowHeight,
  mdiWeight,
} from '@mdi/js';
import { Controller, useForm } from 'react-hook-form';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { mdiFormatListCheckbox } from '@mdi/js/commonjs/mdi';
import productsRepository from 'client-sdk/src/Repository/product-repository';
import handleError from '../../utils/handleError';
import theme from '../../theme';
import handleNotfication from '../../utils/handleNotification';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    item: {
      display: 'flex',
      alignSelf: 'center',
      alignItems: 'center',
      fontWeight: 500,
      width: '100%',
    },
    paper: {
      position: 'relative',
      marginTop: '36px',
      marginBottom: '36px',
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      border: '1px solid #ddd',
      borderRadius: '4px',
    },
    svgIcon: {
      position: 'relative',
      zIndex: 2,
      marginLeft: '60px',
    },
    addIconHolder: {
      position: 'absolute',
      top: '-51px',
      left: 0,
      right: 0,
    },
    addIcon: {
      textAlign: 'center',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        backgroundColor: '#BEDFE0',
      },
    },
    formHolder: {
      marginTop: '4em',
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 400,
      color: '#8A8A8A',
      marginRight: '8px',
      marginTop: '21px',
    },
    inputHolder: {
      flex: 1,
    },
  })
);

interface Props {
  match: any;
}

interface Inputs {
  name: string;
  code: string;
  codingType: string;
  categoryName: string | null;
  unit: string;
  weight: number | null;
  width: number | null;
  length: number | null;
  height: number | null;
}

const Edit: FC<Props> = ({ match }: Props): React.ReactElement => {
  const classes = useStyles();
  const {
    register,
    handleSubmit,
    errors,
    control,
    getValues,
    reset,
    watch,
    setValue
  } = useForm<Inputs>({
    defaultValues: {
      name: '',
      code: '',
      codingType: '',
      categoryName: null,
      unit: '',
      weight: null,
      width: null,
      length: null,
      height: null,
    },
  });
  const [categoryList, setCategoryList] = useState([]);
  const [loading, setLoading] = useState(false as boolean);
  const [productId] = useState(match.params.id);
  const [product, setProduct] = useState<Inputs>(null);

  const submitForm = (values: any) => {
    const { name, code, codingType, categoryName, unit } = product;
    const category = categoryList.find(el => el.name === categoryName)!;
    const { weight, width, length, height } = values;
    const newValues = {
      id: productId,
      categoryCode: category?.code ? category?.code : null,
      name,
      code,
      codingType,
      categoryName,
      unit,
      weight,
      width,
      length,
      height,
      shippingWeight: weight,
      shippingLength: length,
      shippingWidth: width,
      shippingHeight: height,
    };
    handleSubmitProduct(newValues);
  };

  const handleSubmitProduct = async (values: any) => {
    const payload = { body: values };
    try {
      await productsRepository.updateProduct({ payload });
      window.history.back();
      handleNotfication('کالا با موفقیت ویرایش شد', 'success');
    } catch (error) {
      handleError(error.response);
    }
  };

  const getPartnerCategories = async () => {
    const payload = { size: 1000 };
    try {
      setLoading(true);
      const response = await productsRepository.getPartnerCategories({ payload });
      setCategoryList(response.data);
    } catch (error) {
      // handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const getProductWithId = async () => {
    const payload = { id: productId };
    try {
      setLoading(true);
      const response = await productsRepository.getProduct({ payload });
      setProduct(response.data);
      reset(response.data);
      getWeight(response.data)
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      getProductWithId();
      getPartnerCategories();
    }
  }, [productId]);

  const getWeight = async (data: any) => {
    try {
      const response = await (await fetch("http://localhost:3001")).json()
      reset({ ...data, ...response });
    } catch (error) {
      handleNotfication("سیستم وزن غیرفعال است.", "error")
    }
  }

  return (
    <Grid xs={12}>
      <Paper elevation={3} className={classes.paper} variant="elevation" square>
        <div className={classes.addIconHolder}>
          <div className={classes.addIcon}>
            <Icon
              className={classes.svgIcon}
              path={mdiArchiveOutline}
              size={4}
              color={theme.palette.green.main}
            />
          </div>
        </div>
        <Grid className={classes.formHolder} item xs={12}>
          <form onSubmit={handleSubmit(submitForm)}>
            <Grid container spacing={4}>
              <Grid container item xs={12} md={6} lg={3} alignItems="center">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon path={mdiArchiveOutline} size={1} />
                  </span>

                  <div style={{ width: '100%' }}>
                    <TextField
                      type="text"
                      fullWidth
                      id="name"
                      name="name"
                      label="نام کالا"
                      disabled
                      defaultValue={product?.name}
                      InputLabelProps={{
                        shrink: !!product?.name || !!getValues().name,
                      }}
                      inputRef={register({
                        required: false,
                      })}
                      error={errors.name && true}
                    />
                    <FormHelperText error focused>
                      {errors.name && 'نام کالا الزامی میباشد'}
                    </FormHelperText>
                  </div>
                </div>
              </Grid>
              <Grid container item xs={12} md={6} lg={3} alignItems="center">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon path={mdiKey} size={1} />
                  </span>

                  <div style={{ width: '100%' }}>
                    <TextField
                      type="text"
                      fullWidth
                      id="code"
                      name="code"
                      label="کد کالا"
                      disabled
                      InputLabelProps={{
                        shrink: !!product?.code || !!getValues().code,
                      }}
                      inputRef={register({
                        required: false,
                        pattern: /^[A-Za-z\d\-_]+$/,
                      })}
                      error={errors.code && true}
                    />
                    <FormHelperText error focused>
                      {errors.code && 'کد کالا الزامی میباشد'}
                    </FormHelperText>
                  </div>
                </div>
              </Grid>
              <Grid container item xs={12} md={6} lg={3} alignItems="center">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon path={mdiFormatListCheckbox} size={1} />
                  </span>

                  <div style={{ width: '100%' }}>
                    <TextField
                      type="text"
                      fullWidth
                      id="categoryName"
                      name="categoryName"
                      label="دسته بندی"
                      disabled
                      InputLabelProps={{
                        shrink:
                          !!product?.categoryName || !!getValues().categoryName,
                      }}
                      inputRef={register({
                        required: false,
                      })}
                    />
                  </div>
                </div>
              </Grid>
              <Grid container item xs={12} md={6} lg={3} alignItems="center">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon path={mdiFlaskOutline} size={1} />
                  </span>

                  <div style={{ width: '100%' }}>
                    <TextField
                      type="text"
                      fullWidth
                      id="unit"
                      name="unit"
                      disabled
                      InputLabelProps={{
                        shrink: !!product?.unit || !!watch().unit,
                      }}
                      label="واحد اندازه‌گیری"
                      inputRef={register({
                        required: false,
                      })}
                      error={errors.unit && true}
                    />
                    <FormHelperText error focused>
                      {errors.unit && 'واحد اندازه‌گیری الزامی میباشد'}
                    </FormHelperText>
                  </div>
                </div>
              </Grid>
              <Grid container item xs={12} md={6} lg={3} alignItems="center">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon path={mdiWeight} size={1} />
                  </span>

                  <div style={{ width: '100%' }}>
                    <TextField
                      type="text"
                      fullWidth
                      id="weight"
                      name="weight"
                      label="وزن کالا(گرم)"
                      InputLabelProps={{
                        shrink: !!product?.weight || !!watch().weight,
                      }}
                      inputRef={register({
                        required: true,
                      })}
                      error={errors.weight && true}
                    />
                    <FormHelperText error focused>
                      {errors.weight && 'وزن کالا الزامی میباشد'}
                    </FormHelperText>
                  </div>
                </div>
              </Grid>
              <Grid container item xs={12} md={6} lg={3} alignItems="center">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon path={mdiTableRowHeight} size={1} />
                  </span>

                  <div style={{ width: '100%' }}>
                    <TextField
                      type="text"
                      fullWidth
                      id="length"
                      name="length"
                      label="طول کالا(سانتی‌متر)"
                      InputLabelProps={{
                        shrink: !!product?.length || !!watch().length,
                      }}
                      inputRef={register({
                        required: true,
                      })}
                      error={errors.length && true}
                    />
                    <FormHelperText error focused>
                      {errors.length && 'وزن کالا الزامی میباشد'}
                    </FormHelperText>
                  </div>
                </div>
              </Grid>
              <Grid container item xs={12} md={6} lg={3} alignItems="center">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon path={mdiTableColumnWidth} size={1} />
                  </span>

                  <div style={{ width: '100%' }}>
                    <TextField
                      type="text"
                      fullWidth
                      id="width"
                      name="width"
                      label="عرض کالا(سانتی‌متر)"
                      InputLabelProps={{
                        shrink: !!product?.width || !!watch().width,
                      }}
                      inputRef={register({
                        required: true,
                      })}
                      error={errors.width && true}
                    />
                    <FormHelperText error focused>
                      {errors.width && 'عرض کالا الزامی میباشد'}
                    </FormHelperText>
                  </div>
                </div>
              </Grid>
              <Grid container item xs={12} md={6} lg={3} alignItems="center">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon path={mdiRuler} size={1} />
                  </span>

                  <div style={{ width: '100%' }}>
                    <TextField
                      type="text"
                      fullWidth
                      id="height"
                      name="height"
                      label="ارتفاع کالا(سانتی‌متر)"
                      InputLabelProps={{
                        shrink: !!product?.height || !!watch().height,
                      }}
                      inputRef={register({
                        required: true,
                      })}
                      error={errors.height && true}
                    />
                    <FormHelperText error focused>
                      {errors.width && 'ارتفاع کالا الزامی میباشد'}
                    </FormHelperText>
                  </div>
                </div>
              </Grid>
              <Grid container item xs={12} md={6} lg={3} alignItems="center">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon path={mdiFormatListBulletedType} size={1} />
                  </span>

                  <div style={{ width: '100%' }}>
                    <Controller
                      style={{ width: '100%' }}
                      control={control}
                      name="codingType"
                      label="نوع کالا"
                      rules={{ required: false }}
                      as={
                        <TextField
                          disabled
                          select
                          fullWidth
                          error={errors.codingType && true}
                        >
                          <MenuItem value="SINGLE">تکی</MenuItem>
                          <MenuItem value="MASS">انبوه</MenuItem>
                        </TextField>
                      }
                    />
                    <FormHelperText error focused>
                      {errors.codingType && 'نوع کالا الزامی میباشد'}
                    </FormHelperText>
                  </div>
                </div>
              </Grid>
            </Grid>
            <Box mb={3} />
            <Grid container spacing={4}>
              <Grid item xs={8}>
                <Button
                  fullWidth
                  autoFocus
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                >
                  ویرایش
                </Button>
              </Grid>

              <Grid item xs={4}>
                <Button
                  onClick={() => window.history.back()}
                  fullWidth
                  variant="contained"
                  size="large"
                >
                  انصراف
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default Edit;
